/**
 * Feature Analytics Hook
 *
 * Consume el RPC get_features_analytics_overview(p_days) que devuelve
 * métricas de actividad y usuarios activos por funcionalidad de la app.
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface FeatureMetric {
    label: string;
    count?: number;
    users?: number;
    // Campos opcionales según funcionalidad
    total_clubs?: number;
    rsvps?: number;
    total_videos?: number;
    sources?: number;
    published?: number;
    drafts?: number;
    ratings?: number;
    total_materials?: number;
    completed?: number;
    comments?: number;
    reactions?: number;
    comment_users?: number;
    reaction_users?: number;
    total?: number;
    claimed?: number;
    inviters?: number;
    reports?: number;
    pending?: number;
}

export interface FeaturesAnalyticsOverview {
    period_days: number;
    captures: FeatureMetric;
    feed: FeatureMetric;
    quiz: FeatureMetric;
    pvp: FeatureMetric;
    clubs: FeatureMetric;
    events: FeatureMetric;
    academy: FeatureMetric;
    news: FeatureMetric;
    blog: FeatureMetric;
    messaging: FeatureMetric;
    store: FeatureMetric;
    ai_video: FeatureMetric;
    intel_chat: FeatureMetric;
    fishing_planner: FeatureMetric;
    social: FeatureMetric;
    multicatch: FeatureMetric;
    trips: FeatureMetric;
    patrols: FeatureMetric;
    invitations: FeatureMetric;
    moderation: FeatureMetric;
}

export type FeatureKey = keyof Omit<FeaturesAnalyticsOverview, 'period_days'>;

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useFeatureAnalytics(days: number = 30) {
    return useQuery<FeaturesAnalyticsOverview>({
        queryKey: ['admin', 'features-analytics', days],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_features_analytics_overview', {
                p_days: days,
            });
            if (error) throw error;
            const normalized = normalizeRpcResponse(data);
            return (normalized[0] || data) as FeaturesAnalyticsOverview;
        },
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

// ─── Helpers ────────────────────────────────────────────────────────────────

export interface FeatureHealthItem {
    key: FeatureKey;
    label: string;
    activity: number;
    /** Null cuando la fila no mide usuarios de la app (inventario o proceso automático). */
    users: number | null;
    icon: string;
    activityCaption?: string;
    note?: string;
}

const FEATURE_ICONS: Record<FeatureKey, string> = {
    captures: 'Camera',
    feed: 'Eye',
    quiz: 'GraduationCap',
    pvp: 'Swords',
    clubs: 'Users',
    events: 'Calendar',
    academy: 'BookOpen',
    news: 'Newspaper',
    blog: 'FileText',
    messaging: 'MessageCircle',
    store: 'ShoppingBag',
    ai_video: 'Video',
    intel_chat: 'MessageCircle',
    fishing_planner: 'Map',
    social: 'Heart',
    multicatch: 'Layers',
    trips: 'Map',
    patrols: 'Shield',
    invitations: 'UserPlus',
    moderation: 'Flag',
};

export function buildFeatureHealthList(data: FeaturesAnalyticsOverview): FeatureHealthItem[] {
    const keys: FeatureKey[] = [
        'captures', 'feed', 'quiz', 'pvp', 'clubs', 'events',
        'academy', 'news', 'blog', 'messaging', 'store', 'ai_video',
        'intel_chat', 'fishing_planner',
        'social', 'multicatch', 'trips', 'patrols', 'invitations', 'moderation',
    ];

    return keys.map(key => {
        const f = data[key];
        let activity = 0;
        let users: number | null = null;
        let activityCaption: string | undefined;
        let note: string | undefined;

        if (key === 'blog') {
            activity = f.published ?? 0;
            activityCaption = 'Publicados';
            note = 'Inventario de artículos, no uso de la app en el periodo';
        } else if (key === 'news') {
            activity = f.count ?? 0;
            activityCaption = 'Ítems';
            note = 'Ítems ingeridos por el proceso automático, sin usuario de la app';
        } else {
            if (f.count != null) activity = f.count;
            else if (f.published != null) activity = f.published + (f.drafts ?? 0);
            else if (f.total != null) activity = f.total;
            else if (f.reports != null) activity = f.reports;
            else if (f.ratings != null) activity = f.ratings;
            else if (f.comments != null) activity = f.comments + (f.reactions ?? 0);

            if (f.users != null) users = f.users;
            else if (f.inviters != null) users = f.inviters;
            else if (f.comment_users != null) users = f.comment_users;
            else if (activity > 0) note = 'Esta fila no tiene dimensión de usuario';
        }

        return {
            key,
            label: f.label,
            activity,
            users,
            icon: FEATURE_ICONS[key],
            activityCaption,
            note,
        };
    });
}
